import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Table from '../../components/UI/Table';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import AddDeviceModal from './AddDeviceModal';
import EditDeviceModal from './EditDeviceModal';
import apiClient from '../../utils/apiClient';
import Dropdown from '../../components/UI/Dropdown';
import FilterDropdown from '../../components/UI/FilterDropdown';
import bulkConfigIcon from '../../assets/icons/bulk-config-cog.svg';
import devicesSearch from '../../assets/icons/devices-search.svg';
import batteryFullIcon from '../../assets/icons/battery-full.svg';
import batteryHalfIcon from '../../assets/icons/battery-half.svg';
import batteryEmptyIcon from '../../assets/icons/battery-empty.svg';
import './Devices.css';
import SettingsModal from './DeviceSettingModal';
import BulkAddDevicesModal from './BulkAddDevicesModal';
import { useDispatch, useSelector } from 'react-redux';
import { addDevice, removeDevice, clearSelectedDevices } from '../../store/devicesSlice';

export const getBatteryIcon = (batteryVoltage) => {
  if (batteryVoltage > 10.25) {
    return batteryFullIcon;
  } else if (batteryVoltage >= 9.5) {
    return batteryHalfIcon;
  } else {
    return batteryEmptyIcon;
  }
};

const Devices = () => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDevice, setEditDevice] = useState(null);
  const [pagesData, setPagesData] = useState({});
  const [pageCounts, setPageCounts] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isReloading, setIsReloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [openUserDropdown, setOpenUserDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [deviceToRemove, setDeviceToRemove] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [deviceSettings, setDeviceSettings] = useState(null);
  const [showNoSettingsModal, setShowNoSettingsModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [searchResults, setSearchResults] = useState(null);

  const dispatch = useDispatch();
  const selectedCheckedDevices = useSelector((state) => state.devices.selectedDevices);
  const location = useLocation();
  const itemsPerPage = 10;
  const pagesPerFetch = Math.ceil(100 / itemsPerPage);

  // Filter options for the dropdown
  const filterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Latch', value: 'latch' },
    { label: 'Beam', value: 'beam' },
    { label: 'I-Lock Blue', value: 'ilock_blue' }
  ];

  useEffect(() => {
    dispatch(clearSelectedDevices());
  }, [dispatch]);

  // Clear selected devices when filter changes
  useEffect(() => {
    dispatch(clearSelectedDevices());
  }, [selectedFilter, dispatch]);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        await loadBlock(0, 1);
      } catch (error) {
        setErrorMessage('Failed to load devices.');
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitial();
  }, []);

  useEffect(() => {
    const reloadData = async () => {
      try {
        await loadBlock(0, 1);
      } catch (error) {
        setErrorMessage('Failed to reload devices.');
        setShowErrorModal(true);
      } finally {
        setIsReloading(false);
      }
    };

    if (isReloading) {
      reloadData();
    }
  }, [isReloading]);

  const loadBlock = async (blockIndex, desiredPage) => {
    if (pageCounts[blockIndex] && pagesData[desiredPage]) {
      setCurrentPage(desiredPage);
      return;
    }
    try {
      let res;
      if (blockIndex === 0) {
        res = await apiClient.get('/devices');
      } else {
        const prevToken = tokens[blockIndex - 1];
        if (!prevToken) return;
        res = await apiClient.get('/devices', { params: { nextToken: prevToken } });
      }
      const raw = Array.isArray(res.data) ? res.data : res.data.devices;
      const nextToken = res.data.nextToken || null;
      const flat = raw.map(({ Device, UserMapping }) => ({
        ...Device,
        Users: (UserMapping || []).map((u) => ({
          UserId: u.UserId,
          Name: u.Name,
          Phone: u.UserName,
          StartDate: u.StartDate || null,
          EndDate: u.EndDate || null,
          IsDelegate: u.IsDelegate || false,
        })),
      }));
      const groups = {};
      const orphans = [];
      flat.forEach((d) => {
        if (!d.ParentSerialNumber) {
          groups[d.SerialNumber] = { main: d, subs: [] };
        } else {
          orphans.push(d);
        }
      });
      orphans.forEach((d) => {
        const parent = groups[d.ParentSerialNumber];
        if (parent) {
          parent.subs.push(d);
        } else {
          groups[d.SerialNumber] = { main: d, subs: [] };
        }
      });
      const arr = Object.values(groups);
      const pagesCountInBlock = Math.ceil(arr.length / itemsPerPage);
      const newPagesData = { ...pagesData };
      for (let i = 0; i < pagesCountInBlock; i++) {
        const pageNumber = blockIndex * pagesPerFetch + i + 1;
        const startIdx = i * itemsPerPage;
        newPagesData[pageNumber] = arr.slice(startIdx, startIdx + itemsPerPage);
      }
      const newPageCounts = [...pageCounts];
      newPageCounts[blockIndex] = pagesCountInBlock;
      const newTokens = [...tokens];
      newTokens[blockIndex] = nextToken;
      setPagesData(newPagesData);
      setPageCounts(newPageCounts);
      setTokens(newTokens);
      setCurrentPage(desiredPage);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePageChange = (page) => {
    const blockIndex = Math.floor((page - 1) / pagesPerFetch);
    if (pageCounts[blockIndex]) {
      setCurrentPage(page);
    } else {
      loadBlock(blockIndex, page);
    }
  };

  const handleNextBlock = () => {
    const currentBlock = Math.floor((currentPage - 1) / pagesPerFetch);
    const nextBlock = currentBlock + 1;
    if (pageCounts[nextBlock]) {
      setCurrentPage(nextBlock * pagesPerFetch + 1);
    } else {
      loadBlock(nextBlock, nextBlock * pagesPerFetch + 1);
    }
  };

  const handlePrevBlock = () => {
    const currentBlock = Math.floor((currentPage - 1) / pagesPerFetch);
    const prevBlock = currentBlock - 1;
    setCurrentPage(prevBlock * pagesPerFetch + 1);
  };

  const handleDeviceNameClick = async (deviceId) => {
    try {
      const response = await apiClient.get(`/device_settings/${deviceId}`);
      const settings = response.data;
      const slim = settings.map(({ Name, Value }) => ({
        name: Name,
        value: Value,
      }));

      setDeviceSettings(slim);
      setShowSettingsModal(true);
    } catch (error) {
      console.error(error);
      setShowNoSettingsModal(true);
    }
  };

  const handleExportDevices = async () => {
    try {
      const response = await apiClient.get('/export_devices', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'devices.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setErrorMessage('Failed to export devices.');
      setShowErrorModal(true);
    }
  };

  const handleSearchSubmit = async () => {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      const response = await apiClient.get('/devices', {
        params: { serial: searchTerm.trim() }
      });

      const raw = Array.isArray(response.data) ? response.data : response.data.devices || [];

      const flat = raw.map(({ Device, UserMapping }) => ({
        ...Device,
        Users: (UserMapping || []).map((u) => ({
          UserId: u.UserId,
          Name: u.Name,
          Phone: u.UserName,
          StartDate: u.StartDate || null,
          EndDate: u.EndDate || null,
          IsDelegate: u.IsDelegate || false,
        })),
      }));

      const groups = {};
      const orphans = [];
      flat.forEach((d) => {
        if (!d.ParentSerialNumber) {
          groups[d.SerialNumber] = { main: d, subs: [] };
        } else {
          orphans.push(d);
        }
      });
      orphans.forEach((d) => {
        const parent = groups[d.ParentSerialNumber];
        if (parent) {
          parent.subs.push(d);
        } else {
          groups[d.SerialNumber] = { main: d, subs: [] };
        }
      });

      const results = Object.values(groups);
      setSearchResults(results);
      setCurrentPage(1);
    } catch (error) {
      console.error('Search error:', error);
      setErrorMessage('Failed to fetch search results.');
      setShowErrorModal(true);
    }
  };


  const handleAddClick = () => setShowAddModal(true);
  const handleCloseModal = () => setShowAddModal(false);
  const handleBulkClick = () => navigate('/devices/bulk-configuration');
  const handleBulkAddClick = () => setShowBulkAddModal(true);
  const handleCloseBulkAddModal = () => setShowBulkAddModal(false);
  const handleCloseEditDevice = () => {
    setShowEditModal(false);
    setEditDevice(null);
  };

  const handleUserDropdownToggle = (event, userDropId) => {
    event.stopPropagation();
    const rect = event.target.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX,
    });
    setOpenUserDropdown((prev) => (prev === userDropId ? null : userDropId));
  };

  useEffect(() => {
    if (!openUserDropdown) return;

    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenUserDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openUserDropdown]);

  const handleExpandToggle = (serialNumber) => {
    setExpandedRows((prev) => ({
      ...prev,
      [serialNumber]: !prev[serialNumber],
    }));
  };

  const handleCheckboxChange = (serialNumber, deviceKind, deviceName, deviceId) => {
    const isSelected = selectedCheckedDevices.some((d) => d.serialNumber === serialNumber);
    if (isSelected) {
      dispatch(removeDevice(serialNumber));
    } else {
      dispatch(addDevice({ serialNumber, kind: deviceKind, name: deviceName, deviceId }));
    }
  };

  const handleEditDeviceClick = (deviceData) => {
    setEditDevice(deviceData);
    setShowEditModal(true);
  };

  const handleSaveDevice = async (orig, newName, newUsers = []) => {
    try {
      const updatedDevice = { ...orig, DeviceName: newName };
      await apiClient.put(`/devices/${orig.DeviceId}`, updatedDevice);
      const origUsers = orig.Users || [];
      const addedUsers = newUsers.filter((u) => !origUsers.some((o) => o.UserId === u.UserId));
      const deletedUsers = origUsers.filter((o) => !newUsers.some((u) => u.UserId === o.UserId));
      
      // Handle users with updated time access or type changes
      const updatedUsers = newUsers.filter((u) => {
        const origUser = origUsers.find((o) => o.UserId === u.UserId);
        if (!origUser) return false;
        // Check if time access has changed OR user type has changed (permanent ↔ temporary)
        // Use IsDelegate flag to determine if user is temporary, not just presence of dates
        const origIsTemporary = origUser.IsDelegate;
        const newIsTemporary = u.IsDelegate;
        return (origUser.StartDate !== u.StartDate || origUser.EndDate !== u.EndDate || origIsTemporary !== newIsTemporary);
      });

      if (addedUsers.length > 0 || deletedUsers.length > 0 || updatedUsers.length > 0) {
        // Separate users by type for proper backend handling
        // Use IsDelegate flag to determine user type, not just presence of dates
        const permanentAdded = addedUsers.filter(u => !u.IsDelegate);
        const temporaryAdded = addedUsers.filter(u => u.IsDelegate);
        const permanentRemoved = deletedUsers.filter(u => !u.IsDelegate);
        const temporaryRemoved = deletedUsers.filter(u => u.IsDelegate);
        
        // Handle type changes: permanent → temporary or temporary → permanent
        const typeChangedUsers = updatedUsers.map(u => {
          const origUser = origUsers.find(o => o.UserId === u.UserId);
          const origIsTemporary = origUser.IsDelegate;
          const newIsTemporary = u.IsDelegate;
          
          if (origIsTemporary && !newIsTemporary) {
            // temporary → permanent: remove from delegates, add to users
            return { user: u, action: 'tempToPerm' };
          } else if (!origIsTemporary && newIsTemporary) {
            // permanent → temporary: remove from users, add to delegates
            return { user: u, action: 'permToTemp' };
          } else {
            // same type, just update dates
            return { user: u, action: 'updateDates' };
          }
        });
        
        // Process type changes
        typeChangedUsers.forEach(({ user, action }) => {
          if (action === 'tempToPerm') {
            permanentAdded.push(user);
            temporaryRemoved.push(user);
          } else if (action === 'permToTemp') {
            temporaryAdded.push(user);
            permanentRemoved.push(user);
          } else if (action === 'updateDates') {
            // For date updates, we need to remove and re-add with new dates
            if (user.IsDelegate) {
              temporaryRemoved.push(user);
              temporaryAdded.push(user);
            } else {
              permanentRemoved.push(user);
              permanentAdded.push(user);
            }
          }
        });
        
        const payload = {
          Device: {
            DeviceId: orig.DeviceId,
            SerialNumber: orig.SerialNumber,
          },
          AddUsers: permanentAdded.map((u) => ({
            UserId: u.UserId,
            Name: u.Name,
            UserName: u.Phone || u.UserName,
          })),
          RemoveUsers: permanentRemoved.map((u) => ({
            UserId: u.UserId,
            Name: u.Name,
            UserName: u.Phone || u.UserName,
          })),
          AddDelegates: temporaryAdded.map((u) => ({
            DeviceId: orig.DeviceId,
            UserId: u.UserId,
            UserName: u.Phone || u.UserName,
            Name: u.Name,
            Phone: u.Phone || u.UserName,
            StartDate: new Date(u.StartDate).toISOString(),
            EndDate: new Date(u.EndDate).toISOString(),
          })),
          RemoveDelegates: temporaryRemoved.map((u) => ({
            DeviceId: orig.DeviceId,
            UserId: u.UserId,
            UserName: u.Phone || u.UserName,
            Name: u.Name,
            Phone: u.Phone || u.UserName,
            StartDate: new Date(u.StartDate || new Date()).toISOString(),
            EndDate: new Date(u.EndDate || new Date()).toISOString(),
          })),
        };
        await apiClient.put(`/bulk_register_device`, payload);
      }
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
    } catch (err) {
      console.error('Save device error:', err);
      setErrorMessage('Error updating device: ' + (err.response?.data?.message || err.message));
      setShowErrorModal(true);
    }
  };

  const handleRemoveDeviceClick = async (devData) => {
    try {
      await apiClient.delete(`/devices/${devData.DeviceId}`);
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
    } catch (err) {
      setErrorMessage('Error removing device.');
      setShowErrorModal(true);
    }
  };

  const handleAddDevice = async (selectedUsers, serialNumber, deviceName, deviceType, parentSerialNumber) => {
    try {
      if (!serialNumber.trim()) {
        setErrorMessage('Serial number is required.');
        setShowErrorModal(true);
        return;
      }
      if (!selectedUsers || selectedUsers.length === 0) {
        const newDeviceObj = {
          SerialNumber: serialNumber,
          ParentSerialNumber: parentSerialNumber || '',
          DeviceName: deviceName,
          Kind: deviceType,
        };
        await apiClient.post('/devices', newDeviceObj);
      } else {
        const devicePayload = {
          SerialNumber: serialNumber,
          DeviceName: deviceName,
          Kind: deviceType,
          ParentSerialNumber: parentSerialNumber || '',
        };
        const permanentUsers = selectedUsers.filter(u => u.accessType !== 'temporary');
        const temporaryUsers = selectedUsers.filter(u => u.accessType === 'temporary');
        
        await apiClient.post('/bulk_register_device', {
          Device: devicePayload,
          Users: permanentUsers.map((u) => ({
            UserId: u.UserId,
            Name: u.Name,
            UserName: u.Phone,
          })),
          Delegates: temporaryUsers.map((u) => ({
            DeviceId: '', // Will be set by backend
            UserName: u.Phone,
            Name: u.Name,
            Phone: u.Phone,
            StartDate: new Date(u.startDate).toISOString(),
            EndDate: new Date(u.endDate).toISOString(),
          })),
        });
      }
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
      setShowAddModal(false);
    } catch (error) {
      setErrorMessage('Error adding device.');
      setShowErrorModal(true);
    }
  };

  const capitalizeFirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

  const columns = [' ', 'DEVICE', 'USER', 'TYPE', 'STATUS', 'FIRMWARE VERSION', ''];
  const currentGroups = searchResults || pagesData[currentPage] || [];
  const filteredGroups = currentGroups
    .filter(({ main }) => {
      if (selectedFilter === 'all') return true;
      
      // Map filter values to backend expected values
      const filterMap = {
        'latch': 'latch',
        'beam': 'beam', 
        'ilock_blue': 'lock'
      };
      
      const expectedKind = filterMap[selectedFilter];
      return main.Kind?.toLowerCase() === expectedKind;
    })
    .filter(({ main }) => {
      if (!searchTerm.trim()) return true;
      const lowerSearch = searchTerm.toLowerCase();
      return (
        (main.DeviceName || '').toLowerCase().includes(lowerSearch) ||
        (main.SerialNumber || '').toLowerCase().includes(lowerSearch)
      );
    });

  const tableData = [];
  filteredGroups.forEach((group, i) => {
    const mainDev = group.main;
    const subDevs = group.subs || [];
    const isInactive = mainDev.Status?.toLowerCase() === 'inactive';
    const deviceStatus = isInactive ? 'Inactive' : 'Active';
    const isLastRow = i === filteredGroups.length - 1;
    const mainRowIndex = `main-${i}`;

    tableData.push({
      [columns[0]]: (
        <input
          type="checkbox"
          checked={selectedCheckedDevices.some((device) => device.serialNumber === mainDev.SerialNumber)}
          onChange={() =>
            handleCheckboxChange(mainDev.SerialNumber, mainDev.Kind, mainDev.DeviceName, mainDev.DeviceId)
          }
          className="cursor-pointer"
        />
      ),
      [columns[1]]: (
        <div className="device-name-cell">
          <span className="clickable-underline" onClick={() => handleDeviceNameClick(mainDev.DeviceId)}>
            {mainDev.DeviceName}
          </span>
          <div className="device-id" data-testid={`device-id-${mainDev.SerialNumber}`}>{mainDev.SerialNumber}</div>
        </div>
      ),
      [columns[2]]: (() => {
        const devUsers = mainDev.Users || [];
        if (devUsers.length === 0) return '—';
        if (devUsers.length === 1) return devUsers[0].Name;
        const firstUser = devUsers[0].Name;
        const othersCount = devUsers.length - 1;
        const userDropId = `${i}-users`;
        return (
          <div className="user-list">
            <span>{firstUser}</span>
            <span className="more-users" onClick={(e) => handleUserDropdownToggle(e, userDropId)}>
              + {othersCount} more
            </span>
            {openUserDropdown === userDropId && (
              <div
                ref={dropdownRef}
                className="user-dropdown-menu"
                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
              >
                {devUsers.slice(1).map((u) => (
                  <div key={u.Name} className="user-dropdown-item" onClick={() => console.log('Clicked user:', u.Name)}>
                    {u.Name}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })(),
      [columns[3]]: capitalizeFirst(mainDev.Kind) || 'N/A',
      [columns[4]]: (
        <div className="status-battery">
          <span className={isInactive ? 'inactive-badge' : 'active-badge'} data-testid={`status-badge-${mainDev.SerialNumber}`}>
            {deviceStatus}
          </span>
          <img src={getBatteryIcon(mainDev.DeviceHealth)} alt="Battery" className="battery-icon" />
        </div>
      ),
      [columns[5]]: mainDev.FirmwareVersion || '—',
      [columns[6]]: (
        <div className="actions-container">
          <div
            className="more-button"
            data-testid={`more-button-${mainDev.SerialNumber}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpenDropdownIndex(openDropdownIndex === mainRowIndex ? null : mainRowIndex);
            }}
          >
            ⋮
            {openDropdownIndex === mainRowIndex && (
              <Dropdown
                items={[
                  { label: 'Edit', onClick: () => handleEditDeviceClick(mainDev) },
                  {
                    label: 'Remove',
                    onClick: () => {
                      setDeviceToRemove(mainDev);
                      setShowRemoveConfirm(true);
                    },
                    style: { color: 'red' },
                  },
                ]}
                onClose={() => setOpenDropdownIndex(null)}
                placement={isLastRow ? 'top-left' : 'bottom-right'}
              />
            )}
          </div>
          {subDevs.length > 0 && (
            <span
              className={`arrow-toggle ${expandedRows[mainDev.SerialNumber] ? 'rotated' : ''}`}
              onClick={() => handleExpandToggle(mainDev.SerialNumber)}
              data-testid={`expand-toggle-${mainDev.SerialNumber}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                <path d="M7.92773 10.369L12.9277 15.369L17.9277 10.369H7.92773Z" fill="#656565" />
              </svg>
            </span>
          )}
        </div>
      ),
      devId: mainDev.SerialNumber,
      subDevs,
    });

    if (expandedRows[mainDev.SerialNumber] && subDevs.length > 0) {
      subDevs.forEach((sub, j) => {
        const subRowIndex = `sub-${i}-${j}`;
        const subInactive = sub.Status?.toLowerCase() === 'inactive';
        const subStatus = subInactive ? 'Inactive' : 'Active';
        const isLastSubDeviceInGroup = j === subDevs.length - 1;
        const subDropdownPlacement = isLastRow && isLastSubDeviceInGroup ? 'top-left' : 'bottom-right';

        tableData.push({
          [columns[0]]: (
            <input
              type="checkbox"
              checked={selectedCheckedDevices.some((device) => device.serialNumber === sub.SerialNumber)}
              onChange={() => handleCheckboxChange(sub.SerialNumber, sub.Kind, sub.DeviceName, sub.DeviceId)}
              className="cursor-pointer nested-checkbox"
            />
          ),
          [columns[1]]: (
            <div className="sub-device-details">
              <span className="clickable-underline" onClick={() => handleDeviceNameClick(sub.DeviceId)}>
                {sub.DeviceName}
              </span>
              <div className="device-id" data-testid={`device-id-${sub.SerialNumber}`}>{sub.SerialNumber}</div>
            </div>
          ),
          [columns[2]]: capitalizeFirst(sub.Kind),
          [columns[3]]: sub.Type,
          [columns[4]]: (
            <div className="status-battery">
              <span className={subInactive ? 'inactive-badge' : 'active-badge'}>{subStatus}</span>
              <img src={getBatteryIcon(sub.DeviceHealth)} alt="Battery" className="battery-icon" />
            </div>
          ),
          [columns[5]]: sub.FirmwareVersion || '—',
          [columns[6]]: (
            <div className="actions-container">
              <div
                className="more-button"
                data-testid={`more-button-${sub.SerialNumber}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdownIndex(openDropdownIndex === subRowIndex ? null : subRowIndex);
                }}
              >
                ⋮
                {openDropdownIndex === subRowIndex && (
                  <Dropdown
                    items={[
                      { label: 'Edit', onClick: () => handleEditDeviceClick(sub) },
                      {
                        label: 'Remove',
                        onClick: () => {
                          setDeviceToRemove(sub);
                          setShowRemoveConfirm(true);
                        },
                        style: { color: 'red' },
                      },
                    ]}
                    onClose={() => setOpenDropdownIndex(null)}
                    placement={subDropdownPlacement}
                  />
                )}
              </div>
            </div>
          ),
        });
      });
    }
  });

  const currentBlockIndex = Math.floor((currentPage - 1) / pagesPerFetch);
  const startPage = currentBlockIndex * pagesPerFetch + 1;
  const pagesInBlock = pageCounts[currentBlockIndex] || 0;
  const endPage = startPage + pagesInBlock - 1;

  return (
    <div className="devices-page">
      <h1>Devices</h1>
      <div className="devices-header">
        <FilterDropdown 
          options={filterOptions}
          selectedValue={selectedFilter}
          onSelectionChange={setSelectedFilter}
          placeholder="Filter by device type"
        />
      </div>
      <div className="devices-action-bar">
        <div className="devices-left">
          <button className="bulk-config-button" onClick={handleBulkClick}>
            <img src={bulkConfigIcon} alt="" className="bulk-config-icon" />
            Bulk Configuration
          </button>
          <div className="devices-search">
            <img src={devicesSearch} className="devices-search-icon" />
            <input type="text" placeholder="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <button onClick={handleSearchSubmit} className="search-button">Search</button>
            {searchResults && (
              <button className="clear-button" onClick={() => setSearchResults(null)}>
                Clear Search
              </button>
            )}

          </div>
        </div>
        <div className="devices-right">
          <button className="bulk-add-button" onClick={handleBulkAddClick}>
            Bulk Add Devices
          </button>
          <button className="add-button" onClick={handleAddClick}>
            + Add
          </button>
          <button className="export-button" onClick={handleExportDevices}>
            Export
          </button>
        </div>
      </div>
      <Table columns={columns} data={tableData} isLoading={isLoading || isReloading} placeholder="No devices yet" />
      <div className="pagination">
        {currentBlockIndex > 0 && <button onClick={handlePrevBlock}>Prev</button>}
        {Array.from({ length: pagesInBlock }, (_, idx) => startPage + idx).map((page) => (
          <button key={page} onClick={() => handlePageChange(page)} className={page === currentPage ? 'active' : ''}>
            {page}
          </button>
        ))}
        {tokens[currentBlockIndex] && <button onClick={handleNextBlock}>Next</button>}
      </div>
      {showAddModal && <AddDeviceModal onClose={handleCloseModal} onAdd={(users, serial, name, type, parent) => handleAddDevice(users, serial, name, type, parent)} />}
      {showBulkAddModal && <BulkAddDevicesModal onClose={handleCloseBulkAddModal} onBulkAdd={() => setIsReloading(true)} />}
      {showEditModal && editDevice && <EditDeviceModal device={editDevice} onClose={handleCloseEditDevice} onSave={(device, name, assignedUsers) => handleSaveDevice(device, name, assignedUsers)} />}
      {showRemoveConfirm && (
        <ConfirmationModal
          isOpen={showRemoveConfirm}
          onCancel={() => setShowRemoveConfirm(false)}
          onConfirm={() => {
            handleRemoveDeviceClick(deviceToRemove);
            setShowRemoveConfirm(false);
          }}
          message={`Are you sure you want to remove device "${deviceToRemove?.DeviceName}"?`}
          isDanger={true}
          confirmLabel="Remove"
        />
      )}
      {showNoSettingsModal && (
        <ConfirmationModal
          isOpen={showNoSettingsModal}
          onCancel={() => setShowNoSettingsModal(false)}
          onConfirm={() => setShowNoSettingsModal(false)}
          message="No settings found for this device."
          confirmLabel="OK"
          showCancel={false}
          isDanger={false}
        />
      )}
      {showSettingsModal && <SettingsModal isOpen={showSettingsModal} onClose={() => { setShowSettingsModal(false); setDeviceSettings(null); }} deviceSettings={deviceSettings} />}
      {showErrorModal && (
        <ConfirmationModal
          isOpen={showErrorModal}
          onCancel={() => setShowErrorModal(false)}
          onConfirm={() => setShowErrorModal(false)}
          message={errorMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={false}
        />
      )}
    </div>
  );
};

export default Devices;
